/**
 * Copyright Higharc Inc. 2019
 *
 * Depth to view z shader.
 *
 * Show RGBA encoded depth or a depth texture as monochrome color. The color's brightness is relative to the view space
 * z value of the pixel.
 */
import type * as THREE from "three";
import { UVVertexShader } from "./UVVertexShader";

interface DepthToViewZShaderType {
  defines: {
    DEPTH_PACKING: number; // eslint-disable-line @typescript-eslint/naming-convention
    SCALE_DEPTH_VALUES: number; // eslint-disable-line @typescript-eslint/naming-convention
  };
  uniforms: {
    tDepth: THREE.IUniform;
    cameraNear: THREE.IUniform;
    cameraFar: THREE.IUniform;
    perspectiveCamera: THREE.IUniform;
    normalizeViewZ: THREE.IUniform;
    relativeViewZMin: THREE.IUniform;
    relativeViewZMax: THREE.IUniform;
  };
  vertexShader: string;
  fragmentShader: string;
}

export const DepthToViewZShader: DepthToViewZShaderType = {
  defines: {
    DEPTH_PACKING: 1,
    SCALE_DEPTH_VALUES: 0,
  },

  uniforms: {
    tDepth: { value: null },
    cameraNear: { value: 0.1 },
    cameraFar: { value: 10 },
    perspectiveCamera: { value: true },
    normalizeViewZ: { value: true },
    relativeViewZMin: { value: 0 },
    relativeViewZMax: { value: 1 },
  },

  vertexShader: UVVertexShader,

  fragmentShader: `
		uniform sampler2D tDepth;
		uniform float cameraNear;
		uniform float cameraFar;
		uniform bool perspectiveCamera;

		varying vec2 vUv;

		uniform bool normalizeViewZ;

		#if SCALE_DEPTH_VALUES == 1
		uniform float relativeViewZMin;
		uniform float relativeViewZMax;
		#endif

		#include <packing>

		float getDepth( const in vec2 screenPosition ) {

			#if DEPTH_PACKING == 1
			return unpackRGBAToDepth( texture2D( tDepth, screenPosition ) );
			#else
			return texture2D( tDepth, screenPosition ).x;
			#endif

		}

		float getViewZ( const in float depth ) {

			if ( perspectiveCamera ) {
				return perspectiveDepthToViewZ( depth, cameraNear, cameraFar );
			} else {
				return orthographicDepthToViewZ( depth, cameraNear, cameraFar );
			}

		}

		void main() {

			float viewZ = getViewZ ( getDepth( vUv ) );
			if (normalizeViewZ) {
				viewZ = -viewZ / cameraFar;
			}
			#if SCALE_DEPTH_VALUES == 1
			viewZ = (viewZ - relativeViewZMin) / (relativeViewZMax - relativeViewZMin);
			#endif
			gl_FragColor = vec4( vec3( viewZ ), 1.0 );

		}`,
};
